// src/pages/school/fees/components/RequireFeesUnlock.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "react-bootstrap";
import useSchool from "../../../../hooks/useSchool";
import { useAuth } from "../../../../context/AuthContext";
import { isFeeUnlocked, fetchFeeConfig } from "../data/feesAuth";

/**
 * Wrap any Fees page with this. It:
 *  - Allows School Admins through if:
 *      a) no PIN configured, or
 *      b) device is already unlocked for this school+admin.
 *  - Otherwise redirects to /school/fees/login?next=<current path>
 *  - Non-admins see a soft restricted message (you can also redirect if preferred).
 */
export default function RequireFeesUnlock({ children }) {
  const { school } = useSchool();
  const { user, isSchoolAdmin, loading: authLoading } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const schoolId = school?.id || null;
  const uid = user?.uid || null;

  const [checking, setChecking] = useState(true);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    (async () => {
      if (authLoading) return;
      if (!schoolId || !uid) { setChecking(false); return; }

      if (!isSchoolAdmin) {
        setBlocked(true);
        setChecking(false);
        return;
      }

      // If no PIN configured, allow directly
      const cfg = await fetchFeeConfig(schoolId).catch(() => null);
      const hasPin = !!cfg?.pinHash;

      if (!hasPin || isFeeUnlocked(schoolId, uid)) {
        setChecking(false);
        return;
      }

      navigate(`/school/fees/login?next=${encodeURIComponent(pathname)}`, { replace: true });
    })();
  }, [authLoading, isSchoolAdmin, schoolId, uid, pathname, navigate]);

  if (checking) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">Securing Fees…</Card.Body>
      </Card>
    );
  }

  if (blocked) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center">
          <h5 className="mb-2">Restricted</h5>
          <div className="text-muted">Only School Admins can access the Fees module.</div>
        </Card.Body>
      </Card>
    );
  }

  return children;
}
